import { assign, fromPromise, setup } from 'xstate';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import {
  isMaskedContactRefusal,
  isUncertainListingActionError,
  replyRefusalFeedback,
} from '~/features/market/listing-search/errors';
import { MARKET_ACTION_TIMEOUT_MS, MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { SERVICE_OPPORTUNITIES_API as api } from './api';
import type { ServiceCounterInput } from './schemas';
import {
  counterWindowSchema,
  createServiceCounterDraft,
  invalidateCounterSelection,
  serviceCounterPayload,
  type ServiceCounterDraft,
} from './serviceCounterDraft';
import { canAcceptServiceOffer, canCounterServiceOffer } from './serviceOfferActions';

type Thread = Awaited<ReturnType<typeof api.getThread>>;
type Scope = { responseId: number };
type Action =
  | { kind: 'reply'; body: string }
  | { kind: 'withdraw' }
  | { kind: 'reject' }
  | { kind: 'accept'; expectedNegotiationId: number; feeAuthorization?: FeeAuthorization }
  | { kind: 'counter'; input: ServiceCounterInput };
const COUNTER_REVIEW_ERROR =
  'Review the current offer, load availability, select dates and enter a counter message before confirming.';
type Context = Scope & {
  thread: Thread | null;
  draft: string;
  error: string | null;
  errorKind: 'counterReview' | null;
  errorRevision: number;
  counterAvailabilityError: string | null;
  /** A refused reply; shown in the composer instead of the conversation status line. */
  sendError: string | null;
  readError: string | null;
  uncertain: Action['kind'] | null;
  refreshQueued: boolean;
  revision: number;
  /** [MCV-1850] Counts acknowledged declines, so the page toasts each once. */
  declineRevision: number;
  visible: boolean;
  pending: Action | null;
  confirmedAction: Action['kind'] | null;
  counterDraft: ServiceCounterDraft | null;
};

export const serviceConversationMachine = setup({
  types: {
    context: {} as Context,
    input: {} as Scope,
    events: {} as
      | { type: 'REFRESH' }
      | { type: 'EDIT'; text: string }
      | { type: 'SEND' }
      | { type: 'WITHDRAW' }
      | { type: 'REJECT' }
      | { type: 'ACCEPT'; expectedNegotiationId: number; feeAuthorization?: FeeAuthorization }
      | { type: 'OPEN_COUNTER' }
      | { type: 'COUNTER_FOCUSED' }
      | {
          type: 'EDIT_COUNTER';
          terms?: Partial<ServiceCounterInput['negotiationTerms']>;
          messageBody?: string;
        }
      | { type: 'COUNTER_WINDOW'; window: { start: string; end: string } }
      | { type: 'LOAD_COUNTER_AVAILABILITY' }
      | { type: 'TOGGLE_COUNTER_ENTRY'; entryId: number }
      | { type: 'REVIEW_COUNTER' }
      | { type: 'CANCEL_COUNTER' }
      | { type: 'COUNTER' }
      | { type: 'ATTACH' }
      | { type: 'DETACH' }
      | { type: 'DISCARD_UNCERTAINTY' }
      | { type: 'ACKNOWLEDGE_UNCERTAINTY' },
  },
  actors: {
    counterAvailability: fromPromise(
      ({ input, signal }: { input: Scope & { draft: ServiceCounterDraft }; signal: AbortSignal }) =>
        withRequestDeadline(
          (requestSignal) =>
            api.getCounterAvailability(
              input.responseId,
              input.draft.expectedNegotiationId,
              input.draft.window,
              requestSignal
            ),
          MARKET_READ_TIMEOUT_MS,
          signal
        )
    ),
    counter: fromPromise(
      ({
        input,
        signal,
      }: {
        input: Scope & { payload: ServiceCounterInput };
        signal: AbortSignal;
      }) =>
        withRequestDeadline(
          (requestSignal) => api.counter(input.responseId, input.payload, requestSignal),
          MARKET_ACTION_TIMEOUT_MS,
          signal
        )
    ),
    read: fromPromise(({ input, signal }: { input: Scope; signal: AbortSignal }) =>
      withRequestDeadline(
        (requestSignal) => api.getThread(input.responseId, requestSignal),
        MARKET_READ_TIMEOUT_MS,
        signal
      )
    ),
    send: fromPromise(
      ({ input, signal }: { input: Scope & { body: string }; signal: AbortSignal }) =>
        withRequestDeadline(
          (requestSignal) => api.sendReply(input.responseId, input.body, requestSignal),
          MARKET_ACTION_TIMEOUT_MS,
          signal
        )
    ),
    withdraw: fromPromise(({ input, signal }: { input: Scope; signal: AbortSignal }) =>
      withRequestDeadline(
        (requestSignal) => api.withdraw(input.responseId, requestSignal),
        MARKET_ACTION_TIMEOUT_MS,
        signal
      )
    ),
    reject: fromPromise(({ input, signal }: { input: Scope; signal: AbortSignal }) =>
      withRequestDeadline(
        (requestSignal) => api.reject(input.responseId, requestSignal),
        MARKET_ACTION_TIMEOUT_MS,
        signal
      )
    ),
    accept: fromPromise(
      ({
        input,
        signal,
      }: {
        input: Scope & { expectedNegotiationId: number; feeAuthorization?: FeeAuthorization };
        signal: AbortSignal;
      }) =>
        withRequestDeadline(
          (requestSignal) =>
            api.accept(
              input.responseId,
              input.expectedNegotiationId,
              requestSignal,
              ...(input.feeAuthorization ? ([input.feeAuthorization] as const) : [])
            ),
          MARKET_ACTION_TIMEOUT_MS,
          signal
        )
    ),
    markRead: fromPromise(({ input, signal }: { input: Scope; signal: AbortSignal }) =>
      withRequestDeadline(
        (requestSignal) => api.markRead(input.responseId, requestSignal),
        MARKET_ACTION_TIMEOUT_MS,
        signal
      )
    ),
  },
  guards: {
    hasUncertainty: ({ context }) => context.uncertain !== null,
    canSend: ({ context }) =>
      context.thread !== null &&
      context.uncertain === null &&
      context.counterDraft === null &&
      context.draft.trim().length > 0,
    canWithdraw: ({ context }) =>
      context.uncertain === null &&
      context.counterDraft === null &&
      (context.thread?.opportunity.availableActions.includes('withdraw') ?? false),
    canReject: ({ context }) =>
      context.uncertain === null &&
      context.counterDraft === null &&
      (context.thread?.opportunity.availableActions.includes('reject') ?? false),
    canAccept: ({ context, event }) =>
      event.type === 'ACCEPT' &&
      context.uncertain === null &&
      context.counterDraft === null &&
      canAcceptServiceOffer(context.thread?.opportunity) &&
      context.thread?.opportunity.latestNegotiation?.id === event.expectedNegotiationId,
    canEditCounter: ({ context }) => context.uncertain === null && context.counterDraft !== null,
    canReadCounter: ({ context }) =>
      context.uncertain === null &&
      context.counterDraft !== null &&
      canCounterServiceOffer(context.thread?.opportunity) &&
      context.thread?.opportunity.latestNegotiation?.id ===
        context.counterDraft.expectedNegotiationId &&
      counterWindowSchema.safeParse(context.counterDraft.window).success,
    canReviewCounter: ({ context }) =>
      context.uncertain === null &&
      context.counterDraft !== null &&
      canCounterServiceOffer(context.thread?.opportunity) &&
      context.thread?.opportunity.latestNegotiation?.id ===
        context.counterDraft.expectedNegotiationId &&
      serviceCounterPayload(context.counterDraft) !== null,
    canSubmitCounter: ({ context }) =>
      context.uncertain === null &&
      context.counterDraft?.reviewed != null &&
      canCounterServiceOffer(context.thread?.opportunity) &&
      context.thread?.opportunity.latestNegotiation?.id ===
        context.counterDraft.expectedNegotiationId,
  },
  actions: {
    discardUncertainty: assign({
      uncertain: null,
      pending: null,
      draft: '',
      counterDraft: null,
      error: null,
      errorKind: null,
      counterAvailabilityError: null,
      sendError: null,
    }),
  },
}).createMachine({
  id: 'serviceConversation',
  initial: 'dormant',
  context: ({ input }) => ({
    responseId: input.responseId,
    thread: null,
    draft: '',
    error: null,
    errorKind: null,
    errorRevision: 0,
    counterAvailabilityError: null,
    sendError: null,
    readError: null,
    uncertain: null,
    refreshQueued: false,
    revision: 0,
    declineRevision: 0,
    visible: false,
    pending: null,
    confirmedAction: null,
    counterDraft: null,
  }),
  on: {
    ATTACH: { actions: assign({ visible: true }) },
    DETACH: { actions: assign({ visible: false, thread: null, refreshQueued: false }) },
    COUNTER_FOCUSED: {
      actions: assign({
        counterDraft: ({ context }) =>
          context.counterDraft ? { ...context.counterDraft, focusOnOpen: false } : null,
      }),
    },
  },
  states: {
    dormant: {
      entry: assign({
        thread: null,
        counterDraft: ({ context }) => invalidateCounterSelection(context.counterDraft),
      }),
      on: { ATTACH: { target: 'loading', actions: assign({ visible: true }) } },
    },
    loading: {
      entry: assign({
        refreshQueued: false,
        readError: null,
        counterAvailabilityError: null,
        counterDraft: ({ context }) => invalidateCounterSelection(context.counterDraft),
      }),
      invoke: {
        src: 'read',
        input: ({ context }) => ({ responseId: context.responseId }),
        onDone: [
          {
            guard: ({ context }) => context.refreshQueued && context.visible,
            target: 'loading',
            reenter: true,
            actions: assign({
              pending: ({ context }) => (context.uncertain ? context.pending : null),
            }),
          },
          {
            target: 'loaded',
            actions: assign({
              thread: ({ event }) => event.output,
              pending: ({ context }) => (context.uncertain ? context.pending : null),
              confirmedAction: null,
            }),
          },
        ],
        onError: {
          target: 'unavailable',
          actions: assign({
            thread: null,
            readError: ({ context }) =>
              context.confirmedAction
                ? 'Your action succeeded, but the response could not be refreshed. Refresh to try again.'
                : 'Unable to load this response. Refresh to try again.',
          }),
        },
      },
      on: {
        REFRESH: {
          guard: ({ context }) => context.pending === null,
          actions: assign({ refreshQueued: true }),
        },
        ATTACH: {
          guard: ({ context }) => !context.visible,
          actions: assign({ visible: true, refreshQueued: true }),
        },
        DETACH: [
          {
            guard: ({ context }) => context.pending === null,
            target: 'dormant',
            actions: assign({ visible: false, refreshQueued: false }),
          },
          { actions: assign({ visible: false, thread: null, refreshQueued: false }) },
        ],
      },
    },
    loaded: {
      always: [
        { guard: ({ context }) => !context.visible, target: 'dormant' },
        { target: 'ready' },
      ],
    },
    ready: {
      invoke: {
        src: 'markRead',
        input: ({ context }) => ({ responseId: context.responseId }),
        onError: {
          actions: assign({ readError: 'Unable to update read status. Refresh to try again.' }),
        },
      },
      on: {
        OPEN_COUNTER: [
          {
            guard: ({ context }) =>
              context.uncertain === null &&
              context.counterDraft === null &&
              canCounterServiceOffer(context.thread?.opportunity) &&
              context.thread !== null &&
              createServiceCounterDraft(context.thread.opportunity) !== null,
            target: 'counterLoading',
            actions: assign({
              error: null,
              errorKind: null,
              counterDraft: ({ context }) =>
                context.thread ? createServiceCounterDraft(context.thread.opportunity) : null,
            }),
          },
          {
            guard: ({ context }) => context.uncertain === null && context.counterDraft === null,
            actions: assign({
              error:
                'Complete current offer terms and counter permissions are required. Refresh the response before proposing a counteroffer.',
              errorKind: null,
            }),
          },
        ],
        EDIT_COUNTER: {
          guard: 'canEditCounter',
          actions: assign({
            error: null,
            errorKind: null,
            counterDraft: ({ context, event }) =>
              context.counterDraft
                ? {
                    ...context.counterDraft,
                    terms: { ...context.counterDraft.terms, ...event.terms },
                    messageBody: event.messageBody ?? context.counterDraft.messageBody,
                    reviewed: null,
                  }
                : null,
          }),
        },
        COUNTER_WINDOW: {
          guard: 'canEditCounter',
          actions: assign({
            error: null,
            errorKind: null,
            counterAvailabilityError: null,
            counterDraft: ({ context, event }) =>
              context.counterDraft
                ? {
                    ...context.counterDraft,
                    window: { ...event.window },
                    entries: [],
                    selectedIds: [],
                    selectionInitialized: true,
                    selectionLoaded: false,
                    reviewed: null,
                  }
                : null,
          }),
        },
        LOAD_COUNTER_AVAILABILITY: { guard: 'canReadCounter', target: 'counterLoading' },
        TOGGLE_COUNTER_ENTRY: {
          guard: ({ context, event }) =>
            context.uncertain === null &&
            context.counterDraft?.selectionLoaded === true &&
            context.counterDraft.entries.some(
              (entry) => entry.availabilityEntryId === event.entryId
            ),
          actions: assign({
            error: null,
            errorKind: null,
            counterDraft: ({ context, event }) =>
              context.counterDraft
                ? {
                    ...context.counterDraft,
                    selectedIds: context.counterDraft.selectedIds.includes(event.entryId)
                      ? context.counterDraft.selectedIds.filter((id) => id !== event.entryId)
                      : [...context.counterDraft.selectedIds, event.entryId],
                    reviewed: null,
                  }
                : null,
          }),
        },
        REVIEW_COUNTER: [
          {
            guard: 'canReviewCounter',
            actions: assign({
              error: null,
              errorKind: null,
              counterDraft: ({ context }) =>
                context.counterDraft
                  ? {
                      ...context.counterDraft,
                      reviewed: serviceCounterPayload(context.counterDraft),
                    }
                  : null,
            }),
          },
          {
            actions: assign({
              error: COUNTER_REVIEW_ERROR,
              errorKind: 'counterReview',
              errorRevision: ({ context }) => context.errorRevision + 1,
            }),
          },
        ],
        CANCEL_COUNTER: {
          guard: 'canEditCounter',
          actions: assign({
            counterDraft: null,
            error: null,
            errorKind: null,
            counterAvailabilityError: null,
          }),
        },
        COUNTER: {
          guard: 'canSubmitCounter',
          target: 'countering',
          actions: assign({
            error: null,
            errorKind: null,
            sendError: null,
            confirmedAction: null,
            pending: ({ context }) =>
              context.counterDraft?.reviewed
                ? { kind: 'counter', input: context.counterDraft.reviewed }
                : null,
          }),
        },
        DETACH: { target: 'dormant', actions: assign({ visible: false, refreshQueued: false }) },
        REFRESH: 'loading',
        EDIT: {
          guard: ({ context }) => context.uncertain === null,
          actions: assign({ draft: ({ event }) => event.text }),
        },
        SEND: {
          guard: 'canSend',
          target: 'sending',
          actions: assign({
            error: null,
            errorKind: null,
            sendError: null,
            confirmedAction: null,
            pending: ({ context }) => ({ kind: 'reply', body: context.draft.trim() }),
          }),
        },
        WITHDRAW: {
          guard: 'canWithdraw',
          target: 'withdrawing',
          actions: assign({
            error: null,
            errorKind: null,
            sendError: null,
            confirmedAction: null,
            pending: { kind: 'withdraw' },
          }),
        },
        REJECT: {
          guard: 'canReject',
          target: 'rejecting',
          actions: assign({
            error: null,
            errorKind: null,
            sendError: null,
            confirmedAction: null,
            pending: { kind: 'reject' },
          }),
        },
        ACCEPT: [
          {
            guard: 'canAccept',
            target: 'accepting',
            actions: assign({
              error: null,
              errorKind: null,
              sendError: null,
              confirmedAction: null,
              pending: ({ event }) => ({
                kind: 'accept',
                expectedNegotiationId: event.expectedNegotiationId,
                // [MCV-1933] The payer's authorization of the schedule they were shown.
                ...(event.feeAuthorization ? { feeAuthorization: event.feeAuthorization } : {}),
              }),
            }),
          },
          {
            guard: ({ context }) => context.uncertain === null,
            actions: assign({
              error: 'Review the current offer and permissions before accepting.',
              errorKind: null,
            }),
          },
        ],
        // A successful recovery read is required before the user can acknowledge
        // uncertainty. This action never replays a reply or a status mutation.
        ACKNOWLEDGE_UNCERTAINTY: {
          actions: assign({
            uncertain: null,
            error: null,
            errorKind: null,
            sendError: null,
            pending: null,
            counterDraft: ({ context }) =>
              context.uncertain === 'counter'
                ? invalidateCounterSelection(context.counterDraft)
                : context.counterDraft,
          }),
        },
        DISCARD_UNCERTAINTY: { guard: 'hasUncertainty', actions: 'discardUncertainty' },
      },
    },
    counterLoading: {
      entry: assign({
        counterAvailabilityError: null,
        counterDraft: ({ context }) => invalidateCounterSelection(context.counterDraft),
      }),
      invoke: {
        src: 'counterAvailability',
        input: ({ context }) => {
          if (!context.counterDraft) throw new Error('Counter draft is required.');
          return { responseId: context.responseId, draft: context.counterDraft };
        },
        onDone: {
          target: 'ready',
          actions: assign({
            error: ({ context }) => (context.errorKind === 'counterReview' ? null : context.error),
            errorKind: ({ context }) =>
              context.errorKind === 'counterReview' ? null : context.errorKind,
            counterDraft: ({ context, event }) =>
              context.counterDraft
                ? {
                    ...context.counterDraft,
                    entries: event.output.entries,
                    selectionLoaded: true,
                    selectionInitialized: true,
                    selectedIds: context.counterDraft.selectionInitialized
                      ? context.counterDraft.selectedIds.filter((id) =>
                          event.output.entries.some((entry) => entry.availabilityEntryId === id)
                        )
                      : event.output.entries
                          .filter((entry) => entry.currentlySelected)
                          .map((entry) => entry.availabilityEntryId),
                  }
                : null,
          }),
        },
        onError: {
          target: 'ready',
          actions: assign({
            counterAvailabilityError:
              'Unable to load counter availability. Refresh the response or retry availability before reviewing your counter.',
          }),
        },
      },
      on: { DETACH: { target: 'dormant', actions: assign({ visible: false }) } },
    },
    countering: {
      invoke: {
        src: 'counter',
        input: ({ context }) => {
          if (context.pending?.kind !== 'counter') throw new Error('Reviewed counter is required.');
          return { responseId: context.responseId, payload: context.pending.input };
        },
        onDone: {
          target: 'loading',
          actions: assign({
            counterDraft: null,
            confirmedAction: 'counter',
            revision: ({ context }) => context.revision + 1,
          }),
        },
        onError: {
          target: 'loading',
          actions: assign({
            uncertain: ({ event }) =>
              isUncertainListingActionError(event.error) ? 'counter' : null,
            error: ({ event }) =>
              isUncertainListingActionError(event.error)
                ? 'Counteroffer could not be confirmed. Review the refreshed response before taking another action. No counteroffer will be retried automatically.'
                : isMaskedContactRefusal(event.error)
                  ? 'Counteroffer not submitted. Remove contact information from your message or contract terms while identities are masked. Your draft is saved.'
                  : replyRefusalFeedback(
                      event.error,
                      'Counteroffer was refused. Your draft is saved. Check the proposed terms and current availability before reviewing again.'
                    ),
            errorKind: null,
            errorRevision: ({ context }) => context.errorRevision + 1,
          }),
        },
      },
    },
    sending: {
      invoke: {
        src: 'send',
        input: ({ context }) => ({
          responseId: context.responseId,
          body: context.pending?.kind === 'reply' ? context.pending.body : '',
        }),
        onDone: {
          target: 'loading',
          actions: assign({
            draft: '',
            confirmedAction: 'reply',
            revision: ({ context }) => context.revision + 1,
          }),
        },
        onError: [
          {
            guard: ({ event }) => isUncertainListingActionError(event.error),
            target: 'loading',
            actions: assign({
              uncertain: 'reply',
              error:
                'We could not confirm whether your reply was sent. Check the refreshed response before sending again; another send may create a duplicate.',
              errorKind: null,
            }),
          },
          {
            target: 'loading',
            actions: assign({
              sendError: ({ event }) =>
                replyRefusalFeedback(
                  event.error,
                  'Message not sent. Review the message and try again; your draft is saved.'
                ),
            }),
          },
        ],
      },
    },
    withdrawing: {
      invoke: {
        src: 'withdraw',
        input: ({ context }) => ({ responseId: context.responseId }),
        onDone: {
          target: 'loading',
          actions: assign({
            confirmedAction: 'withdraw',
            revision: ({ context }) => context.revision + 1,
          }),
        },
        onError: {
          target: 'loading',
          actions: assign({
            uncertain: ({ event }) =>
              isUncertainListingActionError(event.error) ? 'withdraw' : null,
            error:
              'Withdrawal was not confirmed. Check the refreshed response status before taking another action.',
            errorKind: null,
          }),
        },
      },
    },
    rejecting: {
      invoke: {
        src: 'reject',
        input: ({ context }) => ({ responseId: context.responseId }),
        onDone: {
          target: 'loading',
          actions: assign({
            confirmedAction: 'reject',
            revision: ({ context }) => context.revision + 1,
            declineRevision: ({ context }) => context.declineRevision + 1,
          }),
        },
        onError: {
          target: 'loading',
          actions: assign({
            uncertain: ({ event }) =>
              isUncertainListingActionError(event.error) ? 'reject' : null,
            error:
              'Decline was not confirmed. Check the refreshed response status before taking another action.',
            errorKind: null,
          }),
        },
      },
    },
    accepting: {
      invoke: {
        src: 'accept',
        input: ({ context }) => ({
          responseId: context.responseId,
          expectedNegotiationId:
            context.pending?.kind === 'accept' ? context.pending.expectedNegotiationId : 0,
          feeAuthorization:
            context.pending?.kind === 'accept' ? context.pending.feeAuthorization : undefined,
        }),
        onDone: {
          target: 'loading',
          actions: assign({
            confirmedAction: 'accept',
            revision: ({ context }) => context.revision + 1,
          }),
        },
        onError: {
          target: 'loading',
          actions: assign({
            uncertain: ({ event }) =>
              isUncertainListingActionError(event.error) ? 'accept' : null,
            error: ({ event }) =>
              isUncertainListingActionError(event.error)
                ? 'Acceptance could not be confirmed. Check the refreshed response before taking another action. No acceptance will be retried automatically.'
                : 'Acceptance was refused. Review the current offer, availability and account requirements before confirming again.',
            errorKind: null,
          }),
        },
      },
    },
    unavailable: {
      on: {
        REFRESH: 'loading',
        ATTACH: { target: 'loading', actions: assign({ visible: true }) },
        DISCARD_UNCERTAINTY: { guard: 'hasUncertainty', actions: 'discardUncertainty' },
      },
    },
  },
});
